import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import type {DataTableSortStatus} from "mantine-datatable";

type SortStatus = DataTableSortStatus<any>;
type Filters = Record<string, unknown>;

interface PersistedState<TSortStatus extends SortStatus, TFilters extends Filters> {
    sortStatus: TSortStatus;
    filters: TFilters;
}

interface UseDataTableSessionStateOptions<TSortStatus extends SortStatus, TFilters extends Filters> {
    storageKey: string;
    initialSortStatus: TSortStatus;
    initialFilters?: TFilters;
}

const isBrowser = () => typeof window !== "undefined";

const isObject = (value: unknown): value is Record<string, unknown> => {
    return typeof value === "object" && value !== null && !Array.isArray(value);
};

const isSortStatus = (value: unknown): value is SortStatus => {
    if (!isObject(value)) {
        return false;
    }
    return typeof value.columnAccessor === "string" && (value.direction === "asc" || value.direction === "desc");
};

const parseState = <TSortStatus extends SortStatus, TFilters extends Filters>(
    raw: string | null,
    fallback: PersistedState<TSortStatus, TFilters>
): PersistedState<TSortStatus, TFilters> => {
    if (!raw) {
        return fallback;
    }

    try {
        const parsed = JSON.parse(raw) as Partial<PersistedState<TSortStatus, TFilters>>;
        const sortStatus = isSortStatus(parsed.sortStatus) ? parsed.sortStatus as TSortStatus : fallback.sortStatus;
        const filters = isObject(parsed.filters) ? parsed.filters as TFilters : fallback.filters;
        return {sortStatus, filters};
    } catch {
        return fallback;
    }
};

const readState = <TSortStatus extends SortStatus, TFilters extends Filters>(
    storageKey: string,
    fallback: PersistedState<TSortStatus, TFilters>
): PersistedState<TSortStatus, TFilters> => {
    if (!isBrowser()) {
        return fallback;
    }
    try {
        return parseState(window.sessionStorage.getItem(storageKey), fallback);
    } catch {
        return fallback;
    }
};

export default function useDataTableSessionState<
    TSortStatus extends SortStatus,
    TFilters extends Filters = Record<string, never>
>({
    storageKey,
    initialSortStatus,
    initialFilters,
}: UseDataTableSessionStateOptions<TSortStatus, TFilters>) {
    const initialRef = useRef<PersistedState<TSortStatus, TFilters>>({
        sortStatus: initialSortStatus,
        filters: (initialFilters ?? {}) as TFilters,
    });
    const scopedStorageKey = useMemo(() => `datatable-state:${storageKey}`, [storageKey]);

    const [state, setState] = useState<PersistedState<TSortStatus, TFilters>>(() => readState(scopedStorageKey, initialRef.current));

    useEffect(() => {
        setState(readState(scopedStorageKey, initialRef.current));
    }, [scopedStorageKey]);

    useEffect(() => {
        if (!isBrowser()) {
            return;
        }
        try {
            window.sessionStorage.setItem(scopedStorageKey, JSON.stringify(state));
        } catch {
            // Ignore storage errors and keep the in-memory state.
        }
    }, [scopedStorageKey, state]);

    const setSortStatus = useCallback((value: TSortStatus | ((prev: TSortStatus) => TSortStatus)) => {
        setState(prev => ({
            ...prev,
            sortStatus: typeof value === "function" ? (value as (prev: TSortStatus) => TSortStatus)(prev.sortStatus) : value,
        }));
    }, []);

    const setFilters = useCallback((value: TFilters | ((prev: TFilters) => TFilters)) => {
        setState(prev => ({
            ...prev,
            filters: typeof value === "function" ? (value as (prev: TFilters) => TFilters)(prev.filters) : value,
        }));
    }, []);

    const setFilter = useCallback(<TKey extends keyof TFilters>(key: TKey, value: TFilters[TKey]) => {
        setState(prev => ({
            ...prev,
            filters: {
                ...prev.filters,
                [key]: value,
            },
        }));
    }, []);

    const reset = useCallback(() => {
        setState(initialRef.current);
    }, []);

    const clear = useCallback(() => {
        if (isBrowser()) {
            try {
                window.sessionStorage.removeItem(scopedStorageKey);
            } catch {
                // Ignore storage errors and keep the in-memory state.
            }
        }
        setState(initialRef.current);
    }, [scopedStorageKey]);

    return {
        sortStatus: state.sortStatus,
        setSortStatus,
        filters: state.filters,
        setFilters,
        setFilter,
        reset,
        clear,
    };
}
